import { useState } from 'react';
import { ChevronDown, ChevronRight, CircleOff } from 'lucide-react';
import type { StatementModel, WorkbenchState } from '../../utils/useStatementModel';
import { centsToDollars, excludeProgramPatch, restoreProgramPatch } from '../../utils/useStatementModel';
import { formatDateDisplay } from '../../../utils/date';
import { METHOD_LABELS } from './methodLabels';

interface MobileStatementCardsProps {
  model: StatementModel;
  state: WorkbenchState;
  onChange: (patch: Partial<WorkbenchState>) => void;
  onShareOverride: (programId: string, pct: number | null) => void;
}

/**
 * Narrow-screen rendering of the statement: one stacked card per program
 * (no horizontal scrolling, no drag-reorder), with the same share override,
 * exclude/restore, and payment drill-down as the desktop table.
 */
export function MobileStatementCards({ model, state, onChange, onShareOverride }: MobileStatementCardsProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-2">
      {model.programs.map((line) => (
        <div key={line.programId} className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex items-start justify-between gap-2">
            <button
              type="button"
              onClick={() => toggleExpand(line.programId)}
              className="flex min-w-0 items-center gap-1 text-left text-sm font-medium text-gray-900"
              aria-expanded={expanded.has(line.programId)}
            >
              {expanded.has(line.programId) ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              )}
              <span className="min-w-0">{line.programName}</span>
            </button>
            <button
              type="button"
              onClick={() => onChange(excludeProgramPatch(state, line.programId))}
              title={`Exclude ${line.programName} from the statement`}
              className="shrink-0 rounded p-1 text-gray-300 hover:text-red-600"
            >
              <CircleOff className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-1 pl-5 text-xs text-gray-500">
            {line.nPayments} payment{line.nPayments === 1 ? '' : 's'}
            {line.nRefunds > 0 && `, ${line.nRefunds} refund${line.nRefunds === 1 ? '' : 's'}`}
            {' · '}base <span className="tabular-nums text-gray-700">${centsToDollars(line.baseCents)}</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between pl-5">
            <label className="flex items-center gap-1 text-xs text-gray-500">
              share
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={Number((line.sharePct * 100).toFixed(2))}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v) && v >= 0 && v <= 100) onShareOverride(line.programId, v / 100);
                }}
                className={`w-14 rounded border px-1 py-0.5 text-right text-xs tabular-nums focus:border-emerald-500 focus:outline-none ${
                  line.overridden ? 'border-amber-400 bg-amber-50 font-medium' : 'border-gray-200'
                }`}
                aria-label={`Share % for ${line.programName}`}
              />
              %
              {line.overridden && (
                <button
                  type="button"
                  onClick={() => onShareOverride(line.programId, null)}
                  className="text-amber-600"
                  title="Reset to global share"
                >
                  *
                </button>
              )}
            </label>
            <span className="text-sm font-semibold tabular-nums text-gray-900">
              rent ${centsToDollars(line.rentCents)}
            </span>
          </div>
          {expanded.has(line.programId) && (
            <ul className="mt-2 border-t border-gray-100 pl-5 pt-2">
              {line.drilldown.map((r, i) => (
                <li
                  key={`${r.payment_id}-${r.kind}-${i}`}
                  className={`py-1 text-xs ${r.kind === 'refund' ? 'text-red-700' : 'text-gray-600'}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="min-w-0 truncate font-medium">
                      {formatDateDisplay(r.payment_date)} · {r.student_name ?? '—'}
                    </span>
                    <span className="shrink-0 tabular-nums">${centsToDollars(r.tuition_cents)}</span>
                  </div>
                  <div className="text-gray-400">
                    {r.parent_name ?? '—'} · {METHOD_LABELS[r.payment_method] ?? r.payment_method}
                    {r.kind === 'refund' && ' · refund'}
                    {r.discount_cents !== 0 && (
                      <span> · after −${centsToDollars(Math.abs(r.discount_cents))} discount</span>
                    )}
                    {r.bonus_excluded_cents !== 0 && (
                      <span className="text-amber-700"> · bonus −${centsToDollars(r.bonus_excluded_cents)}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {model.addBacks.map((a) => (
        <div key={a.key} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-600">
          <span className="min-w-0 italic">{a.label}</span>
          <span className="ml-2 shrink-0 tabular-nums">+${centsToDollars(a.rentCents)}</span>
        </div>
      ))}
      {model.adjustments.map((a) => (
        <div key={a.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-600">
          <span className="min-w-0 italic">{a.label} (manual)</span>
          <span className={`ml-2 shrink-0 tabular-nums ${a.rentCents < 0 ? 'text-red-700' : ''}`}>
            {a.rentCents < 0 ? '−' : '+'}${centsToDollars(Math.abs(a.rentCents))}
          </span>
        </div>
      ))}

      {model.excludedPrograms.length > 0 && (
        <div className="rounded-lg border border-dashed border-gray-200 p-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Excluded rows (not in totals)
          </div>
          {model.excludedPrograms.map((line) => (
            <div key={line.programId} className="flex items-center justify-between py-1 text-sm text-gray-400">
              <span className="min-w-0 truncate line-through">{line.programName}</span>
              <button
                type="button"
                onClick={() => onChange(restoreProgramPatch(state, line.programId))}
                className="ml-2 shrink-0 text-xs text-emerald-700"
              >
                restore
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between rounded-lg border-2 border-gray-300 bg-white p-3 text-sm font-semibold text-gray-900">
        <span>TOTAL · base ${centsToDollars(model.totals.baseCents)}</span>
        <span className="tabular-nums">${centsToDollars(model.totals.rentCents)}</span>
      </div>
    </div>
  );
}
